import React, { useEffect, useState } from 'react'

import { Button, Dropdown, Spin, Tooltip, MenuProps } from 'antd'
import { Loading3QuartersOutlined } from '@ant-design/icons'

import i18n from 'i18n'
import classNames from 'classnames'
import moment from 'moment-timezone'
import parse from 'html-react-parser'
import IFace from '../ViewerIFace'

import { getUserEmail } from 'emailThreadViewer/helper/utils'
import { getDisplayEmailHTMLStr, processEmailEvent } from 'emailThreadViewer/helper'
import { formatBytes } from 'utils/ui'

import chevronUp16 from 'assets/images/chevron-up-thick-16.svg'
import chevronDown16 from 'assets/images/chevron-down-thick-16.svg'

import chevronsUp16 from 'assets/images/chevrons-up-16.svg'
import chevronsDown16 from 'assets/images/chevrons-down-16.svg'

import expandText16 from 'assets/images/button-expand-text-16.svg'

import download16SVG from 'assets/images/button-download-16.svg'

import inboxCompanySVG from 'assets/images/priority-inbox-company.svg'
import inboxNewsSVG from 'assets/images/priority-inbox-news.svg'
import inboxImportantSVG from 'assets/images/priority-inbox-important.svg'
import inboxOtherSVG from 'assets/images/priority-inbox-other.svg'

import replySVG from 'assets/images/toolbar/reply.svg'
import replyAllSVG from 'assets/images/toolbar/reply-all.svg'
import forwardSVG from 'assets/images/toolbar/forward.svg'
import moreSVG from 'assets/images/toolbar-more-24.svg'

import calender2SVG from 'assets/images/icon-calendar2.svg'

import IcEdit from 'emailThreadViewer/icons/IcEdit'
import IcDelete from 'emailThreadViewer/icons/IcDelete'
import IcPriority from 'emailThreadViewer/icons/IcPriority'
import IcSensitivity from 'emailThreadViewer/icons/IcSensitivity'
import IcAttachment from 'emailThreadViewer/icons/IcAttachment'
import IcFlagged from 'emailThreadViewer/icons/IcFlagged'
import IcUnflagged from 'emailThreadViewer/icons/IcUnflagged'
import { TextFindableNode } from 'emailThreadViewer/TextFinder'

interface EmailAddress {
  name: string
  email: string
}

interface MeetingInfo {
  dt_start: number
  dt_end: number
  type: number
  timezone?: string
  location?: string
  sender: EmailAddress
  title: string
  new_start_propose_time?: number
  new_end_propose_time?: number
  propose_comment?: string
  enable_response?: boolean
  meeting_response_actions?: number[]
  meeting_proposal_accepted?: boolean
  recurrence?: string
  sequence?: number
}

interface ButtonOptions {
  className?: string
  icon?: React.ReactNode
  key?: string
  onClick?: (e: React.MouseEvent) => void
  disabled?: boolean
  type?: 'text' | 'primary' | 'default' | 'dashed' | 'link'
  size?: 'small' | 'middle' | 'large'
  [key: string]: any
}

interface FoldButtonOptions {
  disabled?: boolean
  upIcon?: React.ReactNode
  downIcon?: React.ReactNode
}

interface DropdownOptions {
  disabled?: boolean
  postfixElem?: React.ReactNode | boolean
}

type MenuItem =
  | {
      label: string | React.ReactNode
      key: string | number
      disabled?: boolean
      type?: never
    }
  | {
      type: 'divider'
      key?: string | number
    }

type ReactObj = any

export function getButtonElemByOptions(this: ReactObj, options: ButtonOptions) {
  const attrs: ButtonOptions = {
    type: 'text',
    size: 'small',
    ...options,
  }
  return <Button {...attrs} />
}

export function getButtonElem(
  this: ReactObj,
  key: string,
  className: string,
  src: string,
  alt: string,
  options?: ButtonOptions,
) {
  const icon = <img src={src} alt={alt} />

  const opts: ButtonOptions = {
    className,
    icon,
    key,
    onClick: (e) => {
      this.onClickBtn(key, e)
    },
  }
  if (options != null && options.disabled != null) {
    opts.disabled = options.disabled
  }
  return getButtonElemByOptions.call(this, opts)
}

export function getFoldButtonElem(
  this: ReactObj,
  key: string,
  className: string,
  src: string,
  alt: string,
  options?: FoldButtonOptions,
) {
  const btnFold = this.state[key] != null ? this.state[key] : this.foldMap[key]
  const loadingKey = `${key}Loading`
  const loading = this.state[loadingKey]

  const upIcon = options?.upIcon || (
    <img src={chevronDown16} alt="unfold" style={{ width: '17px', height: '16px' }} />
  )
  const downIcon = options?.downIcon || (
    <img src={chevronUp16} alt="fold" style={{ width: '17px', height: '16px' }} />
  )

  const foldIcon = btnFold !== true ? upIcon : downIcon

  const icon = (
    <React.Fragment>
      {src && <img src={src} alt={alt} />}
      {loading ? (
        <Spin
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
          indicator={<Loading3QuartersOutlined spin />}
        />
      ) : (
        foldIcon
      )}
    </React.Fragment>
  )

  const opts: ButtonOptions = {
    className,
    icon,
    key,
    onClick: (e) => {
      e.preventDefault()
      e.stopPropagation()

      if (this.state[loadingKey] !== true) {
        const curFold = !btnFold

        this.setState({
          [key]: curFold,
        })

        const apiKey = `on${key}StateChanged`
        if (this[apiKey] != null) {
          this[apiKey](curFold)
        }
      }
    },
  }

  if (options != null && options.disabled != null) {
    opts.disabled = options.disabled
  }
  return getButtonElemByOptions.call(this, opts)
}

export function getDropdownElem(
  this: ReactObj,
  items: MenuItem[],
  className: string,
  src: string,
  alt: string,
  options?: DropdownOptions,
) {
  const menuAttrs: MenuProps = {
    items,
    onClick: (info) => {
      this.onClickBtn(info.key)
      info.domEvent.preventDefault()
      info.domEvent.stopPropagation()
    },
  }

  const dropdownAttrs = {
    overlayClassName: classNames('contextMenu'),
    menu: menuAttrs,
    key: alt,
    trigger: ['click'] as ('click' | 'hover' | 'contextMenu')[],
    onClick: (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
    },
  }

  const icon = <img src={src} alt={alt} />

  const opts: ButtonOptions = {
    className,
    icon,
  }

  const opts0 = options || {}

  if (opts0.disabled != null) {
    opts.disabled = opts0.disabled
  }

  const btnElem = getButtonElemByOptions.call(this, opts)

  let prefixElem: React.ReactNode = null

  if (opts0.postfixElem === false) {
    prefixElem = null
  } else if (opts0.postfixElem == null || opts0.postfixElem === true) {
    prefixElem = <img src={chevronDown16} alt={alt} style={{ width: '17px', height: '16px' }} />
  } else {
    prefixElem = opts0.postfixElem
  }

  return (
    <Dropdown {...dropdownAttrs}>
      <div className={classNames('dropdownWrapper')}>
        {btnElem}
        {prefixElem}
      </div>
    </Dropdown>
  )
}

export function getTopIconsElem(this: ReactObj, isFold: boolean, styles: any) {
  const { isDraft } = this.state
  const { threadItem } = this.props
  const { mailInfo } = threadItem

  const IconButton = ({
    onClick,
    className,
    ...props
  }: {
    onClick?: (e: React.MouseEvent) => void
    className?: string
    children?: React.ReactNode
  }) => {
    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      onClick?.(e)
    }

    return (
      <button className={classNames(styles.iconsBtn, className)} onClick={handleClick} {...props} />
    )
  }

  const DraftBtns = () => (
    <React.Fragment>
      <IconButton onClick={this.editDraft}>
        <IcEdit />
      </IconButton>
      <IconButton onClick={this.deleteDraft}>
        <IcDelete color="#007AFF" />
      </IconButton>
    </React.Fragment>
  )

  const {
    has_attachment: hasAttachment,
    enable_flag: enableFlag,
    priority,
    sensitivity,
    flagged,
  } = mailInfo
  const FlagBtn = () => (
    <IconButton className="flag" onClick={() => threadItem.setFlagged(!flagged)}>
      {flagged ? <IcFlagged /> : <IcUnflagged />}
    </IconButton>
  )

  return (
    <React.Fragment>
      {!isFold && isDraft && <DraftBtns />}
      {sensitivity !== 'normal' && <IcSensitivity />}
      {!!hasAttachment && <IcAttachment className="hasAttachment" />}
      {priority === 'high' && <IcPriority />}
      {enableFlag !== false && (!isFold || !!flagged) && <FlagBtn />}
    </React.Fragment>
  )
}

export function getPriorityInboxElem(
  this: ReactObj,
  priorityInbox: string | undefined,
  styles: any,
  isFold: boolean,
) {
  let pInboxElem: React.ReactNode = null
  switch (priorityInbox) {
    case 'company':
      pInboxElem = <img src={inboxCompanySVG} alt="company" />
      break
    case 'news':
      pInboxElem = <img src={inboxNewsSVG} alt="news" />
      break
    case 'important':
      pInboxElem = getButtonElem.call(
        this,
        'inboxImportant',
        styles.inboxBtn,
        inboxImportantSVG,
        'important',
      )
      break
    case 'other':
      if (isFold !== true) {
        pInboxElem = getButtonElem.call(this, 'inboxOther', styles.inboxBtn, inboxOtherSVG, 'other')
      } else {
        pInboxElem = null
      }
      break
    default:
      pInboxElem = null
  }

  return pInboxElem
}

export function getActionElems(this: ReactObj, styles: any) {
  const {
    is_text_partial: isTextPartial,
    enable_reply_forward: enableReplyForward,
    enable_read: enableRead,
    enable_delete: enableDelete,
    enable_print: enablePrint,
  } = this.props.threadItem.mailInfo

  const forwardAttrs = {
    overlayClassName: classNames('contextMenu'),
    menu: {
      items: [
        {
          label: i18n.t('email.title.forward'),
          key: 'forward',
        },
        {
          label: i18n.t('email.title.forwardAsAttachment'),
          key: 'forwardAsAttachment',
        },
      ],
      onClick: (info) => {
        this.onClickBtn(info.key as string)
        info.domEvent.stopPropagation()
      },
    },
    trigger: ['click'] as ('click' | 'hover' | 'contextMenu')[],
    disabled: isTextPartial,
    onClick: (e) => {
      // 이벤트가 상위로 전달되어 메뉴가 다시 닫히는 문제가 발생함.
      e.stopPropagation()
    },
  }

  const actionElems: React.ReactNode[] =
    enableReplyForward !== false
      ? [
          <Tooltip title={i18n.t('email.title.reply')} key="reply">
            <Button
              type="text"
              size="small"
              onClick={(e) => this.onClickBtn('reply', e)}
              className={styles.replyBtn}
              icon={<img src={replySVG} style={{ opacity: isTextPartial ? 0.3 : 1 }} />}
              disabled={isTextPartial}
            />
          </Tooltip>,
          <Tooltip title={i18n.t('email.title.replyAll')} key="replyAll">
            <Button
              type="text"
              size="small"
              onClick={(e) => this.onClickBtn('replyAll', e)}
              className={styles.replyAllBtn}
              icon={<img src={replyAllSVG} style={{ opacity: isTextPartial ? 0.3 : 1 }} />}
              disabled={isTextPartial}
            />
          </Tooltip>,
          <Tooltip title={i18n.t('email.title.forward')} key="forward">
            <Dropdown {...forwardAttrs}>
              <div className="dropdownWrapper">
                <Button
                  type="text"
                  size="small"
                  className={styles.forwardBtn}
                  icon={<img src={forwardSVG} style={{ opacity: isTextPartial ? 0.3 : 1 }} />}
                  disabled={isTextPartial}
                />
                <img
                  src={chevronDown16}
                  alt="forward"
                  style={{ width: '17px', height: '16px', opacity: isTextPartial ? 0.3 : 1 }}
                />
              </div>
            </Dropdown>
          </Tooltip>,
        ]
      : []

  const moreItems: MenuItem[] = []
  if (enableReplyForward !== false) {
    moreItems.push(
      ...[
        {
          label: i18n.t('email.title.newMail'),
          key: 'newMail',
        },
        {
          label: i18n.t('email.title.sendAgain'),
          key: 'sendAgain',
        },
        { type: 'divider' as const },
      ],
    )
  }

  if (enableDelete !== false) {
    moreItems.push({
      label: i18n.t('email.title.delete'),
      key: 'delete',
    })
  }

  if (enableRead !== false) {
    const { read } = this.props.threadItem.mailInfo
    moreItems.push({
      /* i18next-extract-disable-next-line */
      label: i18n.t(read ? 'email.title.setUnread' : 'email.title.setRead'),
      key: 'setRead',
    })
  }

  if (enableDelete !== false || enableRead !== false) {
    moreItems.push({ type: 'divider' as const })
  }

  moreItems.push(
    ...[
      ...(enablePrint === true
        ? [
            {
              label: i18n.t('email.title.print'),
              key: 'print',
            },
          ]
        : []),
      {
        label: i18n.t('email.title.viewMessageDetails'),
        key: 'viewMessageDetails',
      },
      {
        label: i18n.t('email.title.download'),
        key: 'download',
      },
    ],
  )

  actionElems.push(
    getDropdownElem.call(this, moreItems, styles.moreBtn, moreSVG, 'more', { postfixElem: false }),
  )

  return actionElems
}

export function getAttachmentInfoElem(this: ReactObj, styles: any) {
  const { attachments, foldAttachment, attachmentMinCnt, allFileSize } = this.state

  let AttachmentsInfoElem: React.ReactNode = ''

  if (attachments != null && attachments.length > 1) {
    const downloadBtns = [
      <span
        className={classNames('downloadAll')}
        key="downloadAll"
        role="button"
        tabIndex={-1}
        onKeyDown={() => {}}
        onClick={(e: React.MouseEvent) => {
          this.downloadAll(e)
        }}
      >
        <img src={download16SVG} alt="download" />
        {i18n.t('email.attachment.downloadAll')}
      </span>,
    ]

    let foldAttachmentBtn: React.ReactNode = ''
    if (attachmentMinCnt !== 0 && attachments.length > attachmentMinCnt) {
      const iconSrc = foldAttachment !== true ? chevronsUp16 : chevronsDown16
      foldAttachmentBtn = (
        <Button
          className={classNames(styles.attachmentsFoldBtn)}
          onClick={() => {
            this.setState({
              foldAttachment: !foldAttachment,
            })
          }}
          type="text"
          icon={<img src={iconSrc} alt="fold" />}
        />
      )

      const fileInfo =
        foldAttachment !== true ? (
          <React.Fragment>
            {i18n.t('email.attachment.files', { total: attachments.length })} (
            {formatBytes(allFileSize, 1, true)})
          </React.Fragment>
        ) : (
          <React.Fragment>
            {i18n.t('email.attachment.showAll', { total: attachments.length })} (
            {formatBytes(allFileSize, 1, true)})
          </React.Fragment>
        )

      AttachmentsInfoElem = (
        <div className={classNames(styles.attachmentsInfo)}>
          {foldAttachmentBtn}
          <span
            className={classNames('foldBtn')}
            role="button"
            tabIndex={-1}
            onKeyDown={() => {}}
            onClick={() => {
              this.setState({
                foldAttachment: !foldAttachment,
              })
            }}
          >
            {fileInfo}
          </span>
          {downloadBtns}
        </div>
      )
    } else {
      AttachmentsInfoElem = (
        <div className={classNames(styles.attachmentsInfo)}>
          <span>
            {i18n.t('email.attachment.files', { total: attachments.length })} (
            {formatBytes(allFileSize, 1, true)})
          </span>
          {downloadBtns}
        </div>
      )
    }
  }

  return AttachmentsInfoElem
}

export function getDetailElem(this: ReactObj, styles: any) {
  const {
    attachments,
    meetingInfo,
    bodyElem,
    fullBodyElem,
    foldAttachment,
    isQuotedVisible,
    hasQuoted,
  } = this.state
  const { bodySearch } = this.props

  const attachmentsInfoElem = uiExt.getAttachmentInfoElem(this, styles)
  const calendarElem = meetingInfo != null ? uiExt.getICalElem(this, styles, meetingInfo) : null

  const hasExpandBtn = isQuotedVisible === false && hasQuoted === true
  const body = isQuotedVisible === false ? bodyElem : fullBodyElem || bodyElem

  return (
    <React.Fragment>
      <TextFindableNode shallow={!bodySearch} onFocusChange={this.onFocusChange}>
        {(attachments || []).length > 0 && (
          <>
            <div
              ref={(ref) => this.setAttachmentRef(ref)}
              className={classNames(styles.attachments, { fold: foldAttachment })}
            >
              {attachments}
            </div>
            {attachmentsInfoElem}
          </>
        )}
        {calendarElem}
      </TextFindableNode>
      <div className={classNames(styles.emptyH20)} />
      <div
        className={classNames(styles.viewContainer, 'viewContainer', { hasExpandBtn })}
        ref={this.initRef}
      >
        <TextFindableNode shallow={!bodySearch} onFocusChange={this.onFocusChange}>
          {body}
        </TextFindableNode>

        {hasExpandBtn && (
          <>
            <Button
              className={classNames(styles.expandTextBtn)}
              type="text"
              icon={<img src={expandText16} alt="expand text" />}
              onClick={this.showQuoted}
            />
            {fullBodyElem}
          </>
        )}
      </div>
    </React.Fragment>
  )
}

const getMeetingResActionElem = (
  btnInfo: {
    type: string
    text: string
    callFunc: (e: React.MouseEvent, action: string, resAction?: number) => void
    clsName?: string
  },
  responseActions: number[] | undefined,
  styles: any,
) => {
  const { type, text, callFunc, clsName } = btnInfo
  if (responseActions == null || responseActions.length === 0) {
    return (
      <Button
        className={classNames(styles.actionBtn, clsName)}
        onClick={(e) => {
          callFunc(e, type)
        }}
      >
        {text}
      </Button>
    )
  }

  const items: MenuItem[] = []
  responseActions.forEach((resAction) => {
    switch (resAction) {
      case 8:
        items.push({
          label: i18n.t('meeting.resAction.value8'),
          key: 8,
        })
        break
      case 16:
        items.push({
          label: i18n.t('meeting.resAction.value16'),
          key: 16,
        })
        break
      case 32:
        items.push({
          label: i18n.t('meeting.resAction.value32'),
          key: 32,
        })
        break
      case 128:
        if (items.length > 0) {
          items.push({
            type: 'divider' as const,
          } as any)
        }
        items.push({
          label: i18n.t('meeting.resAction.value128'),
          key: 128,
        })
        break
      default:
    }
  })

  const menuAttrs: MenuProps = {
    items,
    onClick: (info) => {
      callFunc(info.domEvent as any, type, parseInt(info.key as string, 10))
    },
  }

  const dropdownAttrs = {
    overlayClassName: classNames('contextMenu', styles.meetingResActionMenu),
    menu: menuAttrs,
    trigger: ['click'] as ('click' | 'hover' | 'contextMenu')[],
  }

  return (
    <Dropdown {...dropdownAttrs}>
      <Button className={classNames(styles.actionBtn, clsName)}>{text}</Button>
    </Dropdown>
  )
}

export function getICalElem(this: ReactObj, styles: any, meetingInfo: MeetingInfo) {
  const { threadItem } = this.props
  const { mailInfo } = threadItem
  const { from } = mailInfo

  const {
    dt_start,
    dt_end,
    type,
    timezone,
    location,
    sender,
    title: summary,
    new_start_propose_time: proposeStart,
    new_end_propose_time: proposeEnd,
    propose_comment: proposeComment,
    enable_response: enableResponse,
    meeting_response_actions: responseActions,
    meeting_proposal_accepted: proposalAccepted,
    recurrence: recurrenceStr,
    sequence,
  } = meetingInfo

  console.log(' -- getICalElem - meetingInfo: ', responseActions)

  const userEmail = getUserEmail()

  const isOwnerMeeting =
    (type === 0 && sender.email === userEmail) || (type !== 0 && sender.email !== userEmail)
  const hasProposalTime = proposeStart != null && proposeEnd != null
  const hasProposal = proposeComment != null && proposeComment.length > 0

  const startDateStr = moment(dt_start).format('MM.DD')

  const { dateTimeFormat, timeFormat } = IFace.getTimeFormats()

  const getRangeStr = () => {
    let startTime = moment(dt_start)
    let endTime = moment(dt_end)

    if (timezone != null && timezone.length > 0) {
      startTime = startTime.tz(timezone)
      endTime = endTime.tz(timezone)
    }

    let str = ''
    if (startTime.isSame(endTime, 'day')) {
      str = `${startTime.format(dateTimeFormat)} - ${endTime.format(timeFormat)}`
    } else {
      str = `${startTime.format(dateTimeFormat)} - ${endTime.format(dateTimeFormat)}`
    }
    return str
  }

  const TimeRange = () => {
    const [text, setText] = useState(getRangeStr())

    useEffect(() => {
      const onLanguageChanged = () => {
        setTimeout(() => {
          setText(getRangeStr())
        }, 10)
      }
      i18n.on('languageChanged', onLanguageChanged)

      return () => {
        i18n.off('languageChanged', onLanguageChanged)
      }
    }, [])

    return <React.Fragment>{text}</React.Fragment>
  }

  const rangeStr = <TimeRange />

  let proposeRange: string | null = null
  if (hasProposalTime === true) {
    let startPTime = moment(proposeStart)
    let endPTime = moment(proposeEnd)

    if (timezone != null && timezone.length > 0) {
      startPTime = startPTime.tz(timezone)
      endPTime = endPTime.tz(timezone)
    }

    if (startPTime.isSame(endPTime, 'day')) {
      proposeRange = `${startPTime.format(dateTimeFormat)} - ${endPTime.format(timeFormat)}`
    } else {
      proposeRange = `${startPTime.format(dateTimeFormat)} - ${endPTime.format(dateTimeFormat)}`
    }
  }

  let title: React.ReactNode = null
  const recurrenceElem =
    recurrenceStr != null && recurrenceStr.length > 0 ? (
      <div className={classNames(styles.recurrence)}>{recurrenceStr}</div>
    ) : null
  let contentElem: React.ReactNode = null
  let proposalElem: React.ReactNode[] | null = null
  const locationElem =
    location != null && location.length > 0 ? (
      <div className={classNames(styles.location)}>{location}</div>
    ) : null
  const clsProp: Record<string, boolean> = {}

  if (type === 1) {
    clsProp.canceled = true

    const canceledStr = i18n.t('meeting.title.canceled', { summary: '' })
    if (summary.indexOf(canceledStr) !== 0) {
      title = i18n.t('meeting.title.canceled', { summary })
    } else {
      title = summary
    }
  } else if (isOwnerMeeting === false) {
    const organizer = getDisplayEmailHTMLStr(from)

    let statusTxt = ''
    if (type === 2) {
      statusTxt = i18n.t('meeting.status.accepted')
    } else if (type === 3) {
      statusTxt = i18n.t('meeting.status.tentative')
    } else if (type === 4) {
      statusTxt = i18n.t('meeting.status.declined')
    }

    if (sequence != null && sequence > 0) {
      title = parse(i18n.t('meeting.title.updated', { organizer }))
    } else {
      title = parse(i18n.t('meeting.title.invited', { organizer, status: statusTxt }))
    }
    if (enableResponse === true) {
      const acceptBtn = getMeetingResActionElem(
        {
          clsName: classNames(styles.actionBtn, 'accept'),
          type: 'accept',
          text: i18n.t('common.button.accept'),
          callFunc: this.onClickICal,
        },
        responseActions,
        styles,
      )
      const declineBtn = getMeetingResActionElem(
        {
          clsName: 'decline',
          type: 'decline',
          text: i18n.t('common.button.decline'),
          callFunc: this.onClickICal,
        },
        responseActions,
        styles,
      )
      const tentativeBtn = getMeetingResActionElem(
        {
          clsName: 'tentative',
          type: 'tentative',
          text: i18n.t('common.button.tentative'),
          callFunc: this.onClickICal,
        },
        responseActions,
        styles,
      )

      contentElem = (
        <div className={classNames(styles.actions)}>
          {acceptBtn}
          {declineBtn}
          {tentativeBtn}
        </div>
      )
    }
  } else {
    const attendee = getDisplayEmailHTMLStr(from)

    if (type === 2) {
      title = i18n.t('meeting.title.accepted', { attendee })
    } else if (type === 3) {
      title = i18n.t('meeting.title.tentative', { attendee })
    } else if (type === 4) {
      title = i18n.t('meeting.title.declined', { attendee })
    }

    title = parse(String(title || ''))

    if (hasProposal === true) {
      proposalElem = []
      if (proposeComment != null) {
        proposalElem.push(
          <div className={classNames(styles.proposalComment)} key="wrapper">
            {proposeComment}
          </div>,
        )
      }

      if (proposeRange != null) {
        const proposalTitle = parse(i18n.t('meeting.title.proposed', { attendee }))
        proposalElem.push(
          <div className={classNames(styles.invitation, styles.proposal)} key="proposal">
            <div className={classNames(styles.title)}>{proposalTitle}</div>
            <div className={classNames(styles.datetime)}>{proposeRange}</div>
          </div>,
        )

        if (enableResponse === true) {
          if (proposalAccepted !== true) {
            proposalElem.push(
              <div className={classNames(styles.actions)} key="actions">
                <Button
                  className={classNames(styles.actionBtn, 'propose')}
                  onClick={(e) => {
                    this.onClickICal(e, 'propose-accept')
                  }}
                >
                  {i18n.t('common.button.accept')}
                </Button>
              </div>,
            )
          }
        }

        if (proposalAccepted === true) {
          proposalElem.push(
            <div className={classNames(styles.actions)} key="actions">
              {i18n.t('meeting.status.accepted')}
            </div>,
          )
        }
      }
    }
  }

  return (
    <div
      className={classNames(styles.ical)}
      onClick={(e) => {
        processEmailEvent(e)
      }}
    >
      <div className={classNames(styles.icalIcon)}>
        <img
          className={classNames(styles.calenderDateBack)}
          src={calender2SVG}
          alt="calendarDate"
        />
        <div className={classNames(styles.calenderDate)}>{startDateStr}</div>
      </div>
      <div className={classNames(styles.icalContent)}>
        <div className={classNames(styles.invitation, clsProp)}>
          <div className={classNames(styles.title)}>{title}</div>
          <div className={classNames(styles.datetime)}>{rangeStr}</div>
          {locationElem}
          {recurrenceElem}
        </div>
        {contentElem}
        {proposalElem}
      </div>
    </div>
  )
}

const uiExtFunc = {
  getButtonElemByOptions,
  getButtonElem,
  getFoldButtonElem,
  getDropdownElem,
  getTopIconsElem,
  getPriorityInboxElem,
  getActionElems,
  getAttachmentInfoElem,
  getDetailElem,
  getICalElem,
}

const uiExt: Record<string, (reactObj: ReactObj, ...args: any[]) => any> = {}

Object.keys(uiExtFunc).forEach((key) => {
  uiExt[key] = function (reactObj: ReactObj, ...args: any[]) {
    return (uiExtFunc as any)[key].apply(reactObj, args)
  }
})

export default uiExt
